// Shared types and utilities for LiveView hooks.

import type {
  AuthHook,
  CiHook,
  EditorHook,
  OmnisearchHook,
  PrHook,
  ProfileHook,
  SshHook,
  ThemeHook,
  UiHook,
} from "../../../.dox/ui";
import type EasyMDE from "easymde";

// --- Wire-string event unions ---
//
// These mirror the snake_case strings that Phoenix sees on the wire (i.e.
// what `illuminate_*` in `.dox/ui.ex` returns and what every `handle_event/3`
// pattern-matches on). Paradox's --typescript codegen currently emits the
// camelCase *tag* names into the `*PushEvent` type aliases (e.g.
// `"setCode" | "setBlame"` instead of `"set_code" | "set_blame"`), so the
// generated TS unions don't match the actual wire. Until that codegen bug
// is fixed upstream (tracked in FIX_UPSTREAM.md), we keep one hand-rolled
// wire-string union per illuminated dox union here.
//
// (Removed the hand-rolled `*PushEventWire` aliases — paradox
// `--typescript` now emits the illuminate wire strings as the union
// members directly. Import from `.dox/ui` and use the union types as
// the constraint everywhere. A wire-name typo is a tsc error, sourced
// from the same illuminate block the Elixir illuminator reads.)
export type {
  CiPushEvent as CiPushEventWire,
  EditorPushEvent as EditorPushEventWire,
  OmnisearchPushEvent as OmnisearchPushEventWire,
  SearchPushEvent as SearchPushEventWire,
  PrPushEvent as PrPushEventWire,
  ThemePushEvent as ThemePushEventWire,
} from "../../../.dox/ui";

// --- LiveView hook typing ---
//
// LiveViewHook is parameterised by the wire-string union it is allowed to
// send (`Sends`) and receive (`Receives`). Defaults to `string` so third-
// party hooks that don't bother to specialise still compile, but every
// internal hook MUST narrow these — that's the whole point of paradox-
// generated wire types: a typo in either direction is a compile error.
//
// We use a single union per domain for both directions because the dox
// `*PushEvent` enumeration is the wire-form catalog for *both* directions
// (server-emitted push_event AND client-emitted pushEvent map to the same
// handle_event/3 clauses on the Elixir side).

export interface LiveViewHook<
  Receives extends string = string,
  Sends extends string = Receives,
  El extends HTMLElement = HTMLElement,
> {
  el: El;
  pushEvent(event: Sends, payload: Record<string, unknown>): void;
  pushEventTo(
    selectorOrTarget: string | HTMLElement,
    event: string,
    payload: Record<string, unknown>,
    callback?: () => void,
  ): void;
  handleEvent(event: Receives, callback: (payload: unknown) => void): void;
  [key: string]: unknown;
}

// --- Per-hook state interfaces ---
// Typed state for hooks that stash mutable data on `this`.
// Use as intersection types: `LiveViewHook & MarkdownEditorState`.

export interface MarkdownEditorState {
  _editor: EasyMDE | null;
  _modalOpenedHandler: ((e: Event) => void) | null;
  _debounceTimer: ReturnType<typeof setTimeout> | null;
  _flushDispatch: (() => void) | null;
  _form: HTMLFormElement | null;
  _formSubmitHandler: (() => void) | null;
}

export interface RecentCommitsState {
  _lastCount: number;
  _skipNextUpdate: boolean;
}

export interface ToastState {
  _timer: ReturnType<typeof setTimeout> | null;
}

export interface PrTimelineState {
  _focused: number;
  _keyHandler: ((e: KeyboardEvent) => void) | null;
  _lineObserver: ResizeObserver | null;
  _lineDrawn: boolean;
}

export interface ClipboardState {
  _clickHandler: ((e: Event) => void) | null;
}

export interface SshKeyPasteState {
  _dragoverHandler: ((e: Event) => void) | null;
  _dragleaveHandler: (() => void) | null;
  _dropHandler: ((e: DragEvent) => void) | null;
}

export interface CodeRefAutocompleteState {
  _inputHandler: ((e: Event) => void) | null;
  _dropdown: HTMLDivElement | null;
}

// --- Keyboard utilities ---

// Test whether a keyboard event targets an editable element (queries DOM).
// Covers:
//   - native form controls
//   - any contenteditable
//   - CodeMirror 6 (`.cm-editor.cm-focused` — used by the blob editor)
//   - CodeMirror 5 (`.CodeMirror-focused` — bundled by EasyMDE for the
//     PR / comment markdown editors). Without the CM5 check,
//     global shortcuts like `b`, `s`, `y` would `preventDefault` plain
//     letter keys typed inside a markdown editor.
export const isEditableTarget = (e: KeyboardEvent): boolean => {
  const tag = (e.target as HTMLElement).tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    (e.target as HTMLElement).isContentEditable ||
    !!document.querySelector(".cm-editor.cm-focused") ||
    !!document.querySelector(".CodeMirror-focused")
  );
};

// Pure: test modifier-free key press
export const plainKey = (e: KeyboardEvent, key: string): boolean =>
  e.key === key && !e.ctrlKey && !e.metaKey && !e.altKey;

// --- Sidebar persistence ---

// Side effect: persist sidebar width to cookie + CSS var
export const persistSidebarWidth = (w: number): void => {
  document.cookie = `sidebar_w=${w};path=/;max-age=31536000;SameSite=Lax`;
  document.documentElement.style.setProperty("--sidebar-w", `${w}px`);
};

// --- GSAP residual cleanup ---

// Side effect: clear GSAP residual inline styles from sidebar
export const clearGsapResiduals = (el: HTMLElement): void => {
  el.style.opacity = "";
  el.style.overflow = "";
  el.style.minWidth = "";
};

export type {
  AuthHook,
  CiHook,
  EditorHook,
  OmnisearchHook,
  PrHook,
  ProfileHook,
  SshHook,
  ThemeHook,
  UiHook,
};
